import { useState } from 'react';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { useRevit } from '../../contexts/RevitContext';
import './SubAccountExpired.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faArrowLeft, faSpinner, faCheck } from '@fortawesome/free-solid-svg-icons';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const SubAccountExpired = ({ accountName, username }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [planType, setPlanType] = useState(null);
  const { getToken } = useAuth();
  const { user } = useClerk();
  const { setIsSubAccountActive, setUsername, setActiveSubAccountName } = useRevit();

  const handleResubscribe = async (selectedPlanType) => {
    setIsLoading(true);
    setPlanType(selectedPlanType);
    try {
      const token = await getToken();
      const response = await fetch(`https://${SERVER_URL}/api/resubscribe-expired-sub-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          subAccountUsername: username,
          subAccountName: accountName || 'Sub Account',
          planType: selectedPlanType
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('Error creating resubscription checkout session:', error);
      setIsLoading(false);
      setPlanType(null);
    }
  };

  const switchToMainAccount = () => {
    // Show loading state or overlay to prevent UI flicker
    document.body.classList.add('page-transitioning');
    
    // Switch back to the main account
    setIsSubAccountActive(false);
    setUsername(user.id);
    setActiveSubAccountName(user.id);
    window.location.reload();
    setTimeout(() => {
      window.location.reload();
    }, 50);
  };

  return (
    <div className="sub-account-expired-container">
      <div className="expired-card">
        <div className="expired-icon">
          <FontAwesomeIcon icon={faExclamationTriangle} />
        </div>
        
        <h1>Sub-Account Subscription Expired</h1>
        
        <p className="expired-message">
          The subscription for <strong>{accountName || 'this sub-account'}</strong> has expired. 
          Please choose a plan to continue using this sub-account.
        </p>
        
        <div className="subscription-options">
          <div className="subscription-option">
            <h3>Essential</h3>
            <div className="price">
              <span className="currency">$</span>
              <span className="amount">49</span>
              <span className="period">/month</span>
            </div>
            <ul className="features">
              <li><FontAwesomeIcon icon={faCheck} /> Track up to 5 offers</li>
              <li><FontAwesomeIcon icon={faCheck} /> One-Click Link Updates</li>
              <li><FontAwesomeIcon icon={faCheck} /> Performance Dashboard</li>
            </ul>
            <button 
              className="btn-sub-essential"
              onClick={() => handleResubscribe('monthlyEssential')}
              disabled={isLoading}
            >
              {isLoading && planType === 'monthlyEssential' ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Processing...
                </>
              ) : (
                'Get Essential'
              )}
            </button>
          </div>
          
          <div className="subscription-option highlight">
            <div className="recommended-badge">Recommended</div>
            <h3>Pro</h3>
            <div className="price">
              <span className="currency">$</span>
              <span className="amount">99</span>
              <span className="period">/month</span>
            </div>
            <ul className="features">
              <li><FontAwesomeIcon icon={faCheck} /> Unlimited offers</li>
              <li><FontAwesomeIcon icon={faCheck} /> One-Click Link Updates</li>
              <li><FontAwesomeIcon icon={faCheck} /> Performance Dashboard</li>
              <li><FontAwesomeIcon icon={faCheck} /> AI Comment Analysis</li>
              <li><FontAwesomeIcon icon={faCheck} /> AI Video Ideas</li>
            </ul>
            <button 
              className="btn-sub-pro"
              onClick={() => handleResubscribe('monthlyPro')}
              disabled={isLoading}
            >
              {isLoading && planType === 'monthlyPro' ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Processing...
                </>
              ) : (
                'Get Pro'
              )}
            </button>
          </div>
        </div>
        
        <div className="expired-actions">
          <button 
            className="back-to-main-button"
            onClick={switchToMainAccount}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Main Account
          </button>
        </div>
        
        <div className="expired-info">
          <p>
            All data is preserved and will be accessible once resubscribed.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubAccountExpired;
